import { useContext, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { Uploading } from '~/shared/components/Loading';
import Dropzone, { type Accept } from 'react-dropzone';
import imageCompression from 'browser-image-compression';
import {
    fetchFeatureImagePresignedUrl,
    flyerImagePresignedUrl,
    uploadFile,
} from '~/services/campaign';
import { CampaignContext } from '~/context/campaign';
import { useToasts } from 'react-toast-notifications';
import { UseFormSetValue } from 'react-hook-form';

const resizeImage = async (file: File) => {
    const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,
        useWebWorker: true,
    };
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
};

type Props = {
    setValue: UseFormSetValue<any>;
    type: 'event_picture' | 'flyer';
    accept: Accept;
    onUploadComplete?: () => void;
};

export const FundraiserFlyerUpload = ({ setValue, type, accept, onUploadComplete }: Props) => {
    const { addToast } = useToasts();
    const [savingImage, setSavingImage] = useState(false);
    const [{ campaign }] = useContext(CampaignContext);

    const ImgDropzone = () => {
        const handleDrop = async (files: File[]) => {
            const campaignId = campaign.campaignDetail.campaign_id;
            setSavingImage(true);

            try {
                for (const file of files) {
                    // Normalize filename: replace spaces with underscores and remove non-word characters
                    const filenameParts = file.name.split('.');
                    const ext = filenameParts[filenameParts.length - 1];
                    const name = filenameParts[0]
                        .replace(/\s/g, '_')
                        .replace(/(?!\w)./g, '');
                    const normalizedFilename = `${name}.${ext}`;

                    const { fields, url } =
                        type === 'event_picture'
                            ? await fetchFeatureImagePresignedUrl(
                                  normalizedFilename,
                                  campaignId
                              )
                            : await flyerImagePresignedUrl(
                                  normalizedFilename,
                                  campaignId
                              );

                    if (type === 'event_picture') {
                        const resized = await resizeImage(file);
                        await uploadFile(fields, url, resized);
                    } else {
                        await uploadFile(fields, url, file);
                    }
                    setValue(type, url + fields.key);
                }
                onUploadComplete?.();
            } catch (e) {
                addToast('Something went wrong!');
            } finally {
                setSavingImage(false);
            }
        };

        const dropzoneMessage = () => {
            if (savingImage) {
                return (
                    <p className='text-center'>
                        Uploading image <Uploading />
                    </p>
                );
            } else {
                return (
                    <p className='text-center'>
                        Drag 'n' drop a file here, or click to select a file
                    </p>
                );
            }
        };

        return (
            <Dropzone onDrop={handleDrop} accept={accept} multiple={false}>
                {({ getRootProps, getInputProps }) => (
                    <Card
                        className='my-3'
                        style={{ border: '2px dashed #01aeb4' }}
                    >
                        <CardBody>
                            <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                {dropzoneMessage()}
                            </div>
                        </CardBody>
                    </Card>
                )}
            </Dropzone>
        );
    };

    return (
        <div>
            <ImgDropzone />
        </div>
    );
};
